import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productsApi } from '@/services/api';
import { Package, ArrowLeft, Edit, Trash2, Star, Check, Tag, Hash, AlignLeft, Coins } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { UPLOADS_URL } from '@/utils/utils';
import { toast } from 'react-hot-toast';

const SellerProductViewPage: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();

    /*const { data: product, isLoading, isError } = useQuery({
        queryKey: ['seller-product', productId],
        queryFn: () => productsApi.getProductById(Number(productId)),
    });*/
    const { data: product, isLoading, isError } = useQuery({
        queryKey: ['seller-product', productId],
        queryFn: () => productsApi.getProductById(Number(productId)),
        //select: (response) => response?.data,
        select: (response) => {
            //console.log('response', response);
            if (response) {
                return response;
            }
            return null;
        },
        /*onError: () => {
            toast.error('No se pudo cargar el producto');
        }*/
    });
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (isError || !product) {
        return (
            <div className="p-4 text-center text-red-600">
                Error al cargar el producto. Intenta nuevamente.
            </div>
        );
    }
    //console.log('Product:', product);
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN'
        }).format(amount);
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/seller/products')}
                        className="flex items-center text-primary-600 hover:text-primary-800 mb-4"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Volver a mis productos
                    </button>

                    <div className="flex justify-between items-start">
                        <h1 className="text-2xl font-bold text-gray-900">
                            <Package className="inline-block w-6 h-6 mr-2 text-primary-600" />
                            {product.name}
                        </h1>
                        <Badge variant={product.status === 'active' ? 'success' : 'destructive'}>
                            {product.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {/* Imágenes del producto */}
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            Imágenes del Producto
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {product?.images?.length > 0 ? (
                                product.images.map((image: any, index: number) => (
                                    <div key={index} className="relative aspect-square">
                                        <img
                                            src={UPLOADS_URL + image.image_url}
                                            alt={`Imagen ${index + 1} de ${product.name}`}
                                            className="w-full h-full object-cover rounded-lg border border-gray-200"
                                        />
                                        {image.is_primary && (
                                            <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                                Principal
                                            </span>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-full flex items-center justify-center h-32 bg-gray-100 rounded-lg">
                                    <Package className="w-8 h-8 text-gray-400" />
                                    <span className="ml-2 text-gray-500">Sin imágenes</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Información básica */}
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            Información Básica
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <div className="flex items-center text-sm text-gray-500 mb-1">
                                        <Tag className="w-4 h-4 mr-2" />
                                        <span>Nombre del Producto</span>
                                    </div>
                                    <p className="text-gray-900 font-medium">{product.name}</p>
                                </div>

                                <div>
                                    <div className="flex items-center text-sm text-gray-500 mb-1">
                                        <Hash className="w-4 h-4 mr-2" />
                                        <span>SKU</span>
                                    </div>
                                    <p className="text-gray-900 font-mono">{product.sku}</p>
                                </div>

                                <div>
                                    <div className="flex items-center text-sm text-gray-500 mb-1">
                                        <Coins className="w-4 h-4 mr-2" />
                                        <span>Precio</span>
                                    </div>
                                    <p className="text-gray-900 font-medium">{formatCurrency(product.price)}</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <div className="flex items-center text-sm text-gray-500 mb-1">
                                        <Package className="w-4 h-4 mr-2" />
                                        <span>Categoría</span>
                                    </div>
                                    <p className="text-gray-900">{product.category?.name || 'Sin categoría'}</p>
                                </div>

                                <div>
                                    <div className="flex items-center text-sm text-gray-500 mb-1">
                                        <Check className="w-4 h-4 mr-2" />
                                        <span>Stock disponible</span>
                                    </div>
                                    <p className="text-gray-900">{product.stock_quantity} unidades</p>
                                </div>

                                <div>
                                    <div className="flex items-center text-sm text-gray-500 mb-1">
                                        <Star className="w-4 h-4 mr-2" />
                                        <span>Valoración</span>
                                    </div>
                                    <p className="text-gray-900">
                                        {product.average_rating?.toFixed(1) || 'N/A'} ({product.reviews_count || 0} reseñas)
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Descripción */}
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            <AlignLeft className="inline-block w-5 h-5 mr-2 text-primary-600" />
                            Descripción
                        </h2>
                        <div className="prose max-w-none">
                            {product.description ? (
                                <p className="text-gray-700 whitespace-pre-line">{product.description}</p>
                            ) : (
                                <p className="text-gray-500 italic">Sin descripción</p>
                            )}
                        </div>
                    </div>

                    {/* Acciones */}
                    <div className="p-6 bg-gray-50 flex justify-end space-x-4">
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/seller/products/edit/${productId}`)}
                            rightIcon={<Edit className="w-4 h-4 mr-2" />}
                        >

                            Editar Producto
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (confirm('¿Estás seguro de eliminar este producto?')) {
                                    // Lógica para eliminar el producto
                                    toast.promise(
                                        productsApi.deleteProduct(product.id),
                                        {
                                            loading: 'Eliminando producto...',
                                            success: () => {
                                                navigate('/seller/products');
                                                return 'Producto eliminado';
                                            },
                                            error: 'Error al eliminar el producto'
                                        }
                                    );
                                }
                            }}
                            rightIcon={<Trash2 className="w-4 h-4 mr-2" />}
                        >
                            Eliminar Producto
                        </Button>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default SellerProductViewPage;